import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import useAuth from '../../../shared/lib/hooks/useAuth';
import {
  getSessions,
  createSession,
  deleteSession,
  getSessionMessages,
  processSessionMessage,
  generateSessionTitle,
  getRoles,
  getModels,
  getKnowledgeBases,
  getPendingMCPCalls,
  approveMCPCall,
  rejectMCPCall,
  getMcpTools,
  togglePinMessage,
} from '../../../shared/api/aiApi';
import { showNotification } from '../../../shared/snackbar/redux/snackbar.slice';

function AIChatBll({ children, companyId }) {
  const [roles, setRoles] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [models, setModels] = useState([]);
  const [selectedModelId, setSelectedModelId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);

  const titleGeneratedForSession = useRef(new Set());
  const pollIntervalRef = useRef(null);
  const pendingMessageIdsRef = useRef(new Set());
  const currentMCPMessageIdRef = useRef(null);

  const [pendingMCPCalls, setPendingMCPCalls] = useState([]);
  const [currentMCPMessageId, setCurrentMCPMessageId] = useState(null);
  const [mcpToolsMap, setMcpToolsMap] = useState({});
  const [pinnedOnly, setPinnedOnly] = useState(false);

  const activeSessionIdRef = useRef(activeSessionId);

  useEffect(() => { activeSessionIdRef.current = activeSessionId; }, [activeSessionId]);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    pendingMessageIdsRef.current.clear();
    currentMCPMessageIdRef.current = null;
    setCurrentMCPMessageId(null);
    setPendingMCPCalls([]);
  }, []);

  const startPolling = useCallback(() => {
    if (pollIntervalRef.current) return;
    pollIntervalRef.current = setInterval(async () => {
      try {
        const data = await getSessionMessages(activeSessionIdRef.current, 1, 100);
        const sorted = (data.items || []).sort(
          (a, b) => new Date(a.created_at) - new Date(b.created_at),
        );
        setMessages(sorted);

        const pendingUserMsg = sorted.find(
          (m) => m.role === 'assistant' && m.status === 'PENDING_USER',
        );
        if (pendingUserMsg && currentMCPMessageIdRef.current !== pendingUserMsg.id) {
          currentMCPMessageIdRef.current = pendingUserMsg.id;
          setCurrentMCPMessageId(pendingUserMsg.id);
          try {
            const pending = await getPendingMCPCalls(activeSessionIdRef.current, pendingUserMsg.id);
            setPendingMCPCalls(pending.tool_calls || []);
          } catch (err) {

          }
        }

        const stillPending = sorted.some(
          (m) => pendingMessageIdsRef.current.has(m.id) && (m.status === 'PENDING' || m.status === 'PROCESSING'),
        );
        const hasPendingUserMsg = sorted.some(
          (m) => m.role === 'assistant' && m.status === 'PENDING_USER',
        );
        if (!stillPending && !hasPendingUserMsg) {
          stopPolling();
        }
      } catch (err) {

      }
    }, 10000);
  }, [stopPolling]);

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling, activeSessionId]);

  const selectedKbId = useMemo(() => {
    const match = knowledgeBases.find(kb => kb.role_id === selectedRoleId);
    return match ? match.id : null;
  }, [knowledgeBases, selectedRoleId]);

  const dispatch = useDispatch();
  const { user } = useAuth();

  const loadMessages = useCallback(async (sessionId) => {
    try {
      const data = await getSessionMessages(sessionId, 1, 100);
      const sorted = (data.items || []).sort(
        (a, b) => new Date(a.created_at) - new Date(b.created_at),
      );
      setMessages(sorted);
    } catch (err) {
      dispatch(
        showNotification({
          message: 'Ошибка загрузки сообщений сессии',
          severity: 'error',
        }),
      );
      setMessages([]);
    }
  }, []);

  const init = useCallback(async () => {
    setIsLoading(true);
    try {
      const [rolesData, modelsData, sessionsData, kbData, mcpToolsData] = await Promise.all([
        getRoles(1, 100),
        getModels(1, 100),
        getSessions(1, 100),
        getKnowledgeBases(1, 100),
        getMcpTools().catch(() => ({ tools: [] })),
      ]);

      const roleList = rolesData.items || [];
      const modelList = modelsData.items || [];
      const kbList = kbData.items || [];
      const sessionList = (sessionsData.items || []).sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at),
      );

      setRoles(roleList);
      setKnowledgeBases(kbList);
      setModels(modelList);
      setSessions(sessionList);

      const toolsMap = {};
      (mcpToolsData.tools || []).forEach((tool) => {
        toolsMap[tool.id] = tool.title;
      });
      setMcpToolsMap(toolsMap);

      if (roleList.length > 0 && !selectedRoleId) {
        setSelectedRoleId(roleList[0].id);
      }
      if (modelList.length > 0 && !selectedModelId) {
        setSelectedModelId(modelList[0].id);
      }

      if (sessionList.length > 0) {
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        const todaySessions = sessionList.filter(
          (s) => new Date(s.created_at) >= todayStart,
        );

        if (todaySessions.length > 0) {
          setActiveSessionId(todaySessions[0].id);
          await loadMessages(todaySessions[0].id);
        } else {
          setActiveSessionId(null);
          setMessages([]);
        }
      } else {
        setMessages([]);
        setActiveSessionId(null);
      }
    } catch (err) {
      dispatch(showNotification({
        message: 'Не удалось загрузить данные AI-помощника',
        severity: 'error',
      }));
    } finally {
      setIsLoading(false);
    }
  }, [companyId, user, selectedRoleId, selectedModelId]);

  useEffect(() => {
    if (!companyId || !user) return;
    init();
  }, [init, companyId, user]);

  const handleCreateSession = useCallback(async (title) => {
    if (!selectedRoleId) {
      dispatch(showNotification({
        message: 'Сначала выберите роль',
        severity: 'warning',
      }));
      return null;
    }
    try {
      const newSession = await createSession({
        title: title || `Сессия ${new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })} ${new Date().toLocaleDateString('ru-RU')}`,
        roleId: selectedRoleId,
      });
      setSessions(prev => [newSession, ...prev]);
      setActiveSessionId(newSession.id);
      setMessages([]);
      setPendingMCPCalls([]);
      setCurrentMCPMessageId(null);
      currentMCPMessageIdRef.current = null;
      titleGeneratedForSession.current.delete(newSession.id);
      return newSession;
    } catch (err) {
      dispatch(showNotification({
        message: 'Не удалось создать сессию',
        severity: 'error',
      }));
      return null;
    }
  }, [selectedRoleId, dispatch]);

  const handleSelectSession = useCallback(async (sessionId) => {
    if (sessionId === activeSessionId) return;
    setActiveSessionId(sessionId);
    setMessages([]);
    setPendingMCPCalls([]);
    setCurrentMCPMessageId(null);
    currentMCPMessageIdRef.current = null;
    setIsLoading(true);
    await loadMessages(sessionId);
    setIsLoading(false);
  }, [activeSessionId]);

  const handleDeleteSession = useCallback(async (sessionId) => {
    try {
      await deleteSession(sessionId);

      let nextSessionId = null;
      let needCreate = false;

      setSessions(prev => {
        const deletedIndex = prev.findIndex(s => s.id === sessionId);
        const updated = prev.filter(s => s.id !== sessionId);

        if (activeSessionIdRef.current === sessionId) {
          if (updated.length === 0) {
            needCreate = true;
          } else {
            const olderSession = updated[deletedIndex];
            nextSessionId = olderSession ? olderSession.id : updated[updated.length - 1].id;
          }
        }
        return updated;
      });

      if (needCreate) {
        await handleCreateSession();
      } else if (nextSessionId) {
        setActiveSessionId(nextSessionId);
        await loadMessages(nextSessionId);
      }

      dispatch(showNotification({
        message: 'Сессия удалена',
        severity: 'success',
      }));
    } catch (err) {
      dispatch(showNotification({
        message: 'Не удалось удалить сессию',
        severity: 'error',
      }));
    }
  }, [dispatch, handleCreateSession, loadMessages]);

  const handleRoleChange = useCallback((roleId) => {
    setSelectedRoleId(roleId);
  }, []);

  const handleModelChange = useCallback((modelId) => {
    setSelectedModelId(modelId);
  }, []);

  const handleSendMessage = useCallback(async (text) => {
    if (!text.trim() || !selectedModelId) return;

    let sessionId = activeSessionId;
    if (!sessionId) {
      const newSession = await handleCreateSession();
      if (!newSession) return;
      sessionId = newSession.id;
    }

    setIsSending(true);
    try {
      const result = await processSessionMessage(sessionId, {
        content: text,
        modelId: selectedModelId,
        kbId: selectedKbId,
      });

      setMessages(prev => [...prev, result.user_message]);

      if (result.user_message.status === 'PENDING' || result.user_message.status === 'PROCESSING') {
        pendingMessageIdsRef.current.add(result.user_message.id);
        startPolling();
      }

      if (result.assistant_message) {
        setMessages(prev => [...prev, result.assistant_message]);

        if (result.assistant_message.status === 'PENDING_USER') {
          currentMCPMessageIdRef.current = result.assistant_message.id;
          setCurrentMCPMessageId(result.assistant_message.id);
          try {
            const pending = await getPendingMCPCalls(sessionId, result.assistant_message.id);
            setPendingMCPCalls(pending.tool_calls || []);
          } catch (err) {
            dispatch(
              showNotification({
                message: 'Ошибка при вызове инструмента',
                severity: 'error',
              }),
            );
          }
        }
      }

      if (!titleGeneratedForSession.current.has(sessionId)) {
        titleGeneratedForSession.current.add(sessionId);
        generateSessionTitle(sessionId).then(({ title }) => {
          setSessions(prev => prev.map(s =>
            s.id === sessionId ? { ...s, title } : s
          ));
        }).catch(() => {});
      }
    } catch (err) {
      dispatch(showNotification({
        message: 'Ошибка при отправке сообщения',
        severity: 'error',
      }));
    } finally {
      setIsSending(false);
    }
  }, [activeSessionId, selectedModelId, selectedKbId, dispatch, startPolling, handleCreateSession]);

  const handleApproveMCPCall = useCallback(async (callId) => {
    const sessionId = activeSessionIdRef.current;
    const msgId = currentMCPMessageIdRef.current;
    if (!sessionId || !msgId) return;
    try {
      await approveMCPCall(sessionId, msgId, callId);
      const pending = await getPendingMCPCalls(sessionId, msgId);
      setPendingMCPCalls(pending.tool_calls || []);
      if (!pending.tool_calls || pending.tool_calls.length === 0) {
        currentMCPMessageIdRef.current = null;
        setCurrentMCPMessageId(null);
      }
    } catch (err) {
      dispatch(showNotification({
        message: 'Ошибка при подтверждении вызова инструмента',
        severity: 'error',
      }));
    }
  }, [dispatch]);

  const handleRejectMCPCall = useCallback(async (callId) => {
    const sessionId = activeSessionIdRef.current;
    const msgId = currentMCPMessageIdRef.current;
    if (!sessionId || !msgId) return;
    try {
      await rejectMCPCall(sessionId, msgId, callId);
      const pending = await getPendingMCPCalls(sessionId, msgId);
      setPendingMCPCalls(pending.tool_calls || []);
      if (!pending.tool_calls || pending.tool_calls.length === 0) {
        currentMCPMessageIdRef.current = null;
        setCurrentMCPMessageId(null);
      }
    } catch (err) {
      dispatch(showNotification({
        message: 'Ошибка при отклонении вызова инструмента',
        severity: 'error',
      }));
    }
  }, [dispatch]);

  const handleTogglePin = useCallback(async (messageId, isPinned) => {
    const sessionId = activeSessionIdRef.current;
    if (!sessionId) return;
    try {
      await togglePinMessage(sessionId, messageId, isPinned);
      setMessages(prev =>
        prev.map(m => (m.id === messageId ? { ...m, is_pinned: isPinned } : m)),
      );
      const data = await getSessions(1, 100);
      const sessionList = (data.items || []).sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at),
      );
      setSessions(sessionList);
    } catch (err) {
      //console.error('Ошибка при закреплении сообщения:', err);
      dispatch(showNotification({
        message: 'Не удалось изменить статус сообщения',
        severity: 'error',
      }));
    }
  }, [dispatch]);

  const suggestedQuestions = [
    'Чем ты можешь мне помочь?',
    'Какая статистика по уборке комбайнами за вчера?',
    'Покажи выполненные маршруты автобусами за неделю',
    'Проанализируй производительность комбайнов и другой техники за неделю?',
    'Выгрузи урожайность и влажность по полям, уборка которых была в июле 2025 года',
  ];

  const value = {
    messages,
    isLoading,
    isSending,
    roles,
    selectedRoleId,
    handleRoleChange,
    models,
    selectedModelId,
    handleModelChange,
    sessions,
    activeSessionId,
    handleCreateSession,
    handleSelectSession,
    handleDeleteSession,
    handleSendMessage,
    suggestedQuestions,
    pendingMCPCalls,
    currentMCPMessageId,
    handleApproveMCPCall,
    handleRejectMCPCall,
    mcpToolsMap,
    handleTogglePin,
    pinnedOnly,
    setPinnedOnly,
  };

  return React.Children.map(children, child =>
    React.cloneElement(child, { aiChatContext: value })
  );
}

AIChatBll.propTypes = {
  children: PropTypes.node.isRequired,
  companyId: PropTypes.number,
};

AIChatBll.defaultProps = {
  companyId: null,
};

export default AIChatBll;
